/** @module collections */
'use strict';

import Log from 'lib/log';
import escapeStringRegex from 'escape-string-regexp';

const log = Log('base-collection');

function createParseQuery(collection) {
    let model                = collection.model;
    let query                = JSON.parse(JSON.stringify(collection._query));
    let defaultIncludes      = collection.defaultIncludes || [];
    let pagination           = collection.getPagination();
    let sort                 = collection.getSort();
    let searchableAttributes = collection.searchableAttributes || [];
    let searchTerm           = collection.getSearchTerm();
    let parseQuery           = new Parse.Query(model);

    // properties of the query object have precedence over pagination and
    // order configs until we can unify that logic into a single query object
    if (pagination) {
        query.limit = query.limit || pagination.limit;
        query.skip  = query.skip  || pagination.offset;
    }

    if (sort && sort.key) {
        query.order = query.order || (
            (sort.order === 'descending' ? '-' : '') + sort.key
        );
    }

    if (defaultIncludes.length) {
        query.include = query.include || defaultIncludes;
    }

    ['where', 'include', 'keys', 'limit', 'skip', 'order'].forEach(attr => {
        if (query[attr]) {
            parseQuery[`_${attr}`] = query[attr];
        }

        if (['include', 'keys', 'order'].indexOf(attr)) {
            if (query[attr] && typeof query[attr].split === 'function') {
                parseQuery[`_${attr}`] = query[attr].split(',');
            }
        }
    });

    if (searchTerm && searchTerm.length && searchableAttributes.length) {
        let term = '(' + searchTerm.match(/\S+/g).map(
            unescaped => escapeStringRegex(unescaped)
        ).join('|') + ')';

        parseQuery._orQuery(searchableAttributes.map(
            attr => new Parse.Query(model).matches(attr, term, 'i')
        ));
    }

    if (typeof collection.postProcessQuery === 'function') {
        parseQuery = collection.postProcessQuery(parseQuery);
    }

    return parseQuery;
}

module.exports = Parse.Collection.extend(
/** @lends module:collections.BaseCollection */
{

    /**
     * The class name of the models that the collection holds
     * @type {String}
     * @protected
     */
    className: null,

    /**
     * If enabled, models removed from the collection will be removed from the
     * server as well as soon as {@link module:collections.BaseCollection#save}
     * is called
     *
     * @type {Boolean}
     * @default
     * @protected
     */
    syncRemove: false,

    /**
     * An array of references to any removed models from the collection. If
     * {@link module:collections.BaseCollection#syncRemove} is true, these
     * models will be deleted on save
     *
     * @type {Object[]}
     * @default
     * @protected
     */
    removedModels: [],

    /**
     * List of searchable attributes by which we can query the backend
     * to get a subset of models where the value of any of these attributes
     * matches the searchTerm provided.
     *
     * See also {@link module:collections.BaseCollection#searchTerm}
     * @type {String[]}
     * @default []
     * @protected
     */
    searchableAttributes: [],

    /**
     * The code sometimes depends that the collection populates some of the
     * pointers stored in the models. We can set a sensitive default of
     * includes as an array that we know (unless overriden) will be included
     * in the models
     *
     * @type {String[]}
     * @default []
     * @protected
     */
    defaultIncludes: [],

    /**
     * Key used to sort by. Must be a valid attribute of whatever models the
     * collection holds.
     *
     * @type {String}
     * @default
     * @protected
     */
    sortKey:       null,

    /**
     * Order in which the collection is sorted.
     * Value can be 'ascending' or 'descending'. If sortOrder is a truthy
     * value different from 'ascending', it is always considered as
     * 'descending'. IE, if sortOrder === 'whatever' the collection will be
     * sorted in descending order (provided sortKey is set)
     *
     * @type {String}
     * @default
     * @protected
     */
    sortOrder:     null,

    /**
     * A search term used to filter the collection. If set, subsequent
     * calls to {@link module:collections.BaseCollection#fetch} will
     * try to include it in the base query object. But it ultimately depends
     * on the subclasses overriding
     * {@link module:collections.BaseCollection#querySearchTerm}
     *
     * @type {String}
     * @default
     * @protected
     */
    searchTerm:    null,

    /**
     * @typedef {Object} module:collections.BaseCollection~Pagination
     * @prop {number} offset
     * @prop {number} [limit=5000]
     * @prop {number} page
     * @prop {number} pages
     * @prop {number} count
     */

    /**
     * Pagination config object.
     * @type {module:collections.BaseCollection~Pagination}
     * @protected
     */
    pagination: null,

    /**
     * The model constructor
     * @type {Object}
     * @protected
     */
    model: null,

    /**
     * @typedef {Object} module:collections.BaseCollection~Query
     * @prop {Object=} where - The where clause of the query. In it's simplest
     *  form it's a set of key-value pairs where the key is a model attribute
     *  and the value is matched for equality against persisted values. Example:
     *  <br>
     *  To fetch all rows where the name equals to 'John':
     *  <pre><code>{where: {name : 'John'}}</code></pre>
     *  <br><br>
     *
     *  The where clause supports a set of operators that look and work
     *  pretty much like a mongodb query. Namely $or, $ne, $in, etc. For a
     *  comprehensive list of supported operators, see:
     *  {@link http://parseplatform.github.io/docs/rest/guide/#queries}<br><br>
     *
     *  A few gotchas you need to know:
     *  <ul>
     *      <li>
     *          If the attribute you are querying by
     *          is a "pointer" you can't compare it against a model instance.
     *          You need to turn the model into "pointer object" first. See
     *          {@link module:models.BaseModel#toPointerObj}. Example:
     *  <pre><code>{where: {somePointerAttr: model.toPointerObj()}}</code></pre>
     *      </li>
     *      <li>
     *          If the attribute is a date, you need to turn it into something
     *          Parse understands. Querying by date looks something like this:
     *  <pre><code>{where: {
     *      date: {
     *          __type: 'Date',
     *          iso:    new Date().toISOString()
     *      }
     *  }}
     *  </code></pre>
     *      </li>
     *  </ul>
     * @prop {Number} limit - The amount of rows to fetch
     * @prop {Number} skip - The offset in Parse parlance.
     * @prop {String|Array<String>} include - An array or a comma separated
     *  list of includes. Pointers will be populated by the query if
     *  specified in this list
     * @prop {String|Array<String>} sort - An array or a comma separated
     *  list of attributes to sort by. If the attribute name is preceded by
     *  a minus sign, the order is reversed. IE:
     *  <code>{order: '-createdAt'}</code> will sort by the createdAt
     *  attribute in descending order.
     */

    /**
     * An object literal expression of the base Parse.Query object used to
     * fetch data
     *
     * @type {module:collections.BaseCollection~Query}
     * @private
     */
    _query: {},

    /**
     * The base parse query for this collection
     *
     * @type {external:Parse.Query}
     * @protected
     */
    query: null,

    /**
     * @typedef {Object} module:collections.BaseCollection~initOptions
     * @prop {Boolean} syncRemove - If true models in this collection
     *  that are removed from it, are later deleted from the server if the
     *  collection is saved
     * @prop {String} sortKey
     * @prop {String} sortKey - See
     *  {@link module:collections.BaseCollection.sortKey}
     * @prop {String} sortOrder - See
     *  {@link module:collections.BaseCollection.sortOrder}
     * @prop {module:collections.BaseCollection~Pagination} pagination -
     *  See {@link module:collections.BaseCollection.pagination}
     * @prop {module:collections.BaseCollection~Query} query
     */

    /**
     * @constructs
     * @augments external:Parse.Collection
     * @param {Array<external:Parse.Object>=} models
     * @param {module:collections.BaseCollection~initOptions} options
     */
    initialize(models, options) {
        this.pagination || (this.pagination = {});
        options = _.defaults(options || {}, {
            syncRemove: false,
            sortKey:    null,
            sortOrder:  null,
            pagination: {
                offset: this.pagination.offset || 0,
                limit:  this.pagination.limit || 5000,
                page:   this.pagination.page || 0,
                pages:  this.pagination.pages || 0,
                count:  this.pagination.count || 0
            },
            query:      null
        });

        this.className = this.model.prototype.className || '';

        this.query = null;

        this.syncRemove    = (options.syncRemove === true);
        this.removedModels = [];
        if (this.syncRemove === true) {
            log.info(
                `${this.className} collection configured to sync removed ` +
                `models`
            );
            this.on('remove', this.destroyLater, this);
        }

        if (options.pagination && typeof options.pagination === 'object') {
            _.extend(this.pagination, _.omit(options.pagination, [
                'count', 'pages', 'page'
            ]));
        }

        // TODO As with pagination this can be all included in the query
        // object.
        if (options.sortKey) {
            this.sortKey = options.sortKey;
        }
        if (options.sortOrder) {
            this.sortOrder = options.sortOrder;
        }
        if (options.searchTerm) {
            this.searchTerm = options.searchTerm;
        }
        if (options.query) {
            this._query = options.query || {};
        }

        return this;
    },

    /**
     * Whenever the models in this collection change (add, remove, reset, etc)
     * comparator is called to sort them based on
     * {@link module:collections.BaseCollection#sortKey} and
     * {@link module:collections.BaseCollection#sortOrder}.
     *
     * If sortKey is set, this method does calls to
     * {@link module:collections.BaseCollection#valueComparator} to ultimately
     * compare the two values of the specified key.
     *
     * @instance
     * @param {Object} a - A model instance
     * @param {Object} b - Another model to compare to
     */
    comparator(a, b) {
        let keys;

        if (!_.isString(this.sortKey)) {
            return 0;
        }

        keys = this.sortKey.split('.');

        _(keys).each(key => {
            if (
                typeof (a || {}).get !== 'function' ||
                typeof (b || {}).get !== 'function'
            ) {
                a = 0;
                b = 0;
                return;
            }

            a = a.get(key);
            b = b.get(key);
        });

        return this.valueComparator(a, b);
    },

    /**
     * Compare two values mainly for the purpose of sorting the collection.
     * Unless overriden, it's generally always called by
     * {@link module:collections.BaseCollection#comparator}
     *
     * It is assumed that the parameters are of the same type or that can be
     * compared against each other.
     *
     * @instance
     * @param {*} a - Some value
     * @param {*} b - Another value to compare against.
     * @returns {Number} -1, 1 or 0.
     */
    valueComparator(a, b) {
        let order = this.sortOrder || 'ascending';

        if (typeof a === 'string') {
            a = a.toLowerCase();
        }
        if (typeof b === 'string') {
            b = b.toLowerCase();
        }

        if (a instanceof Date) {
            a = a.getTime();
        }
        if (b instanceof Date) {
            b = b.getTime();
        }

        return (order === 'ascending') ? (
            a < b ? -1 :
            a > b ? 1  :
            0
        ) : (
            a < b ? 1 :
            a > b ? -1  :
            0
        );
    },

    /**
     * @typedef {Object} module:collections.BaseCollection~sortConfig
     * @prop {String|null} key - The collection's sortKey
     * @prop {String|null} order - The collection's sortOrder
     */

    /**
     * Get the sort config of this collection
     *
     * @instance
     * @returns {module:collections.BaseCollection~sortConfig}
     */
    getSort() {
        return {
            key:   this.sortKey || null,
            order: this.sortOrder || null
        };
    },

    /*
     * @instance
     * @returns {String}
     */
    getSearchTerm() {
        return this.searchTerm || '';
    },

    /**
     * Overriden Parse.Collection.save() method.
     * Firstly, only dirty or new  models are saved, instead of the whole
     * collection. Secondly, if syncREmove is enabled, any model that was
     * removed from the instance is destroyed from the server.
     *
     * @instance
     * @returns {external:Parse.Promise<module:collections.BaseCollection>}
     */
    save() {
        log.info('saving dirty models');

        return Parse.Promise.when(
            _.map(this.filter(function(model) {
                return (model.dirty() || model.isNew());
            }), function(model) {
                return model.save();
            })
        ).then(function() {
            log.info('models saved');

            if (!this.syncRemove || this.removedModels.length === 0) {
                return Parse.Promise.as(true);
            }

            log.info('destroying removed models');
            return Parse.Object.destroyAll(this.removedModels);
        }.bind(this)).then(function() {
            log.info('done saving');

            this.removedModels = [];
            return Parse.Promise.as(this);
        }.bind(this));
    },

    /**
     * Should be overriden when subclassing. Meant to create a query that
     * includes the search term in it.
     *
     * @instance
     * @returns {external:Parse.Query}
     */
    querySearchTerm() {
        if (this.getSearchTerm()) {
            log.warn('Method not implemented in subclass. Not searching.');
        }

        return this.query;
    },

    /**
     * @instance
     * @param {Object=} options - A backbone compatible options object,
     *  extended.
     * @param {module:collections.BaseCollection~Query} options.query
     * @returns {external:Parse.Promise<module:collections.BaseCollection>}
     */
    fetch(options = {query: null}) {
        // override our current _query with the new one if provided
        if (options.query) {
            this._query = options.query;
        }

        this.query = createParseQuery(this);

        //If you are wondering (like I did), you can't use .always() to
        //reset the collections query. It breaks the thenable chain
        return Parse.Collection.prototype.fetch.apply(this, arguments)
            .then(() => {
                return Parse.Promise.as(this);
            }, err => {
                log.error(this.className, 'collection unable to fetch:', err);
                return Parse.Promise.error(arguments);
            });
    },

    /**
     * @instance
     * @returns {external:Parse.Promise<Number>}
     */
    fetchCount() {
        if (!this.query) {
            this.query = createParseQuery(this);
        }

        return this.query.count();
    },

    /**
     * @instance
     * @returns {external:Parse.Promise<module:collections.BaseCollection>}
     */
    fetchPage(page, options) {
        return this.fetchCount().then(count => {
            this.pagination.count  = count;
            this.pagination.page   = page || 0;
            this.pagination.offset = (
                this.pagination.page * this.pagination.limit
            );
            this.pagination.pages = Math.ceil(
                this.pagination.count / this.pagination.limit
            );
        }).then(() => this.fetch(options).then(() => {
            return Parse.Promise.as(this);
        }));
    },

    /**
     * Get pagination data
     *
     * @instance
     * @returns {module:collections.BaseCollection~Pagination}
     */
    getPagination() {
        return this.pagination || {};
    },

    /**
     * Save model reference to
     * {@link module:collections.BaseCollection#removedModels} so that it is
     * destroyed whenever the collection is saved
     *
     * @todo TODO validate that the model already belongs or belonged to the
     * collection. Make private?
     * @instance
     * @returns {module:collections.BaseCollection} this
     */
    destroyLater(model) {
        if (!model.id) {
            return;
        }

        log.info(`model ${model.id} will be deleted when collection syncs`);
        this.removedModels.push(model);
        return this;
    },

    /**
     * @instance
     * @returns {Boolean} True if the models in this collection are all new
     *  models
     */
    isNew() {
        return this.reduce(function(memo, model) {
            return memo && model.isNew();
        }, true);
    },

    /**
     * @instance
     * @returns {Boolean} True if the models in this collection are all valid
     */
    isValid() {
        return this.reduce(function(memo, model) {
            return memo && model.isValid();
        }, true);
    },

    /**
     * Alias for {@link module:collections.BaseCollection#dirty}
     *
     * @instance
     * @returns {Boolean} True if at least one model in the collection is
     *  dirty, new or there are removed models pending to be destroyed
     */
    isDirty() {
        return this.dirty();
    },

    /**
     * @instance
     * @returns {Boolean} True if at least one model in the collection is
     *  dirty, new or there are removed models pending to be destroyed
     */
    dirty() {
        return this.reduce(function(memo, model) {
            return memo || (model.dirty() || model.isNew());
        }, false) || (this.removedModels.length > 0);
    }
}, {});



// WEBPACK FOOTER //
// ./app/collections/Base.js